import React from "react";
import { Link } from "react-router-dom";
import ScrollToTop from "../components/scroll-to-top";
import SEO from "../components/seo";
import Layout from "../layouts";

const NotFoundPage = () => {
    return (
        <React.Fragment>
            <Layout>
                <SEO
                    title="404 - Page Not Found"
                    description="The page you are looking for does not exist."
                />

                {/* Inject keyframes for animations */}
                <style>{`
                    @keyframes float {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(-15px); }
                    }
                    @keyframes glowPulse {
                        0%, 100% { text-shadow: 0 0 30px rgba(99,102,241,0.4), 0 0 60px rgba(99,102,241,0.2); }
                        50% { text-shadow: 0 0 50px rgba(99,102,241,0.8), 0 0 100px rgba(99,102,241,0.4); }
                    }
                    @keyframes fadeInUp {
                        from { opacity: 0; transform: translateY(40px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes lineScan {
                        0% { transform: translateY(-100%); }
                        100% { transform: translateY(100vh); }
                    }
                    .not-found-btn {
                        display: inline-flex;
                        align-items: center;
                        gap: 10px;
                        padding: 14px 40px;
                        border-radius: 50px;
                        font-weight: 700;
                        font-size: 16px;
                        letter-spacing: 0.05em;
                        text-transform: uppercase;
                        background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
                        color: #fff;
                        border: none;
                        text-decoration: none;
                        position: relative;
                        overflow: hidden;
                        transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                        box-shadow: 0 10px 40px rgba(99, 102, 241, 0.45),
                                    0 0 0 1px rgba(255,255,255,0.1) inset;
                    }
                    .not-found-btn::before {
                        content: '';
                        position: absolute;
                        inset: 0;
                        background: linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 100%);
                        transition: opacity 0.3s ease;
                    }
                    .not-found-btn:hover {
                        transform: translateY(-4px) scale(1.03);
                        box-shadow: 0 20px 60px rgba(99, 102, 241, 0.6),
                                    0 0 0 1px rgba(255,255,255,0.15) inset;
                        color: #fff;
                        text-decoration: none;
                    }
                    .not-found-btn:hover::before {
                        opacity: 0;
                    }
                    .not-found-btn svg {
                        transition: transform 0.3s ease;
                    }
                    .not-found-btn:hover svg {
                        transform: translateX(5px);
                    }
                    .error-badge {
                        display: inline-block;
                        padding: 6px 18px;
                        border-radius: 100px;
                        font-size: 12px;
                        font-weight: 700;
                        letter-spacing: 0.25em;
                        text-transform: uppercase;
                        background: rgba(99, 102, 241, 0.15);
                        border: 1px solid rgba(99, 102, 241, 0.4);
                        color: #a5b4fc;
                        margin-bottom: 28px;
                    }
                `}</style>

                <div
                    style={{
                        position: "relative",
                        minHeight: "100vh",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {/* Full-Screen Background Animation */}
                    <div
                        style={{
                            position: "fixed",
                            inset: 0,
                            zIndex: 0,
                            background: `url(${process.env.PUBLIC_URL}/images/anime.gif) center center / cover no-repeat`,
                        }}
                    />

                    {/* Multi-layer overlay for cinematic depth */}
                    <div
                        style={{
                            position: "fixed",
                            inset: 0,
                            zIndex: 1,
                            background:
                                "radial-gradient(ellipse at center, rgba(15,23,42,0.55) 0%, rgba(8,10,25,0.88) 100%)",
                        }}
                    />

                    {/* Subtle scan line effect */}
                    <div
                        style={{
                            position: "fixed",
                            inset: 0,
                            zIndex: 2,
                            backgroundImage:
                                "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)",
                            pointerEvents: "none",
                        }}
                    />

                    {/* Content */}
                    <div
                        style={{
                            position: "relative",
                            zIndex: 10,
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "80px 20px",
                        }}
                    >
                        <div
                            style={{
                                maxWidth: "600px",
                                width: "100%",
                                textAlign: "center",
                                animation: "fadeInUp 0.8s ease forwards",
                            }}
                        >
                            {/* Glass Card */}
                            <div
                                style={{
                                    background: "rgba(15, 23, 42, 0.5)",
                                    backdropFilter: "blur(24px) saturate(180%)",
                                    WebkitBackdropFilter:
                                        "blur(24px) saturate(180%)",
                                    border: "1px solid rgba(255, 255, 255, 0.08)",
                                    borderRadius: "28px",
                                    padding: "56px 48px",
                                    boxShadow:
                                        "0 32px 64px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04) inset, 0 1px 0 rgba(255,255,255,0.1) inset",
                                }}
                            >
                                {/* Badge */}
                                <div className="error-badge">Error</div>

                                {/* 404 Number */}
                                <div
                                    style={{
                                        fontSize: "clamp(100px, 18vw, 160px)",
                                        fontWeight: "900",
                                        lineHeight: "1",
                                        marginBottom: "4px",
                                        background:
                                            "linear-gradient(135deg, #f8fafc 0%, #c7d2fe 40%, #818cf8 100%)",
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                        backgroundClip: "text",
                                        animation:
                                            "glowPulse 3s ease-in-out infinite",
                                        letterSpacing: "-0.04em",
                                    }}
                                >
                                    404
                                </div>

                                {/* Divider */}
                                <div
                                    style={{
                                        width: "60px",
                                        height: "3px",
                                        background:
                                            "linear-gradient(90deg, transparent, #6366f1, transparent)",
                                        margin: "16px auto 28px",
                                        borderRadius: "2px",
                                    }}
                                />

                                {/* Headline */}
                                <h1
                                    style={{
                                        fontSize: "clamp(22px, 4vw, 30px)",
                                        fontWeight: "700",
                                        color: "#f1f5f9",
                                        marginBottom: "16px",
                                        letterSpacing: "-0.02em",
                                    }}
                                >
                                    Page Not Found
                                </h1>

                                {/* Body text */}
                                <p
                                    style={{
                                        fontSize: "16px",
                                        color: "#94a3b8",
                                        lineHeight: "1.75",
                                        maxWidth: "420px",
                                        margin: "0 auto 40px",
                                    }}
                                >
                                    Looks like you&apos;ve ventured into the
                                    void. The page you&apos;re searching for has
                                    either been moved, renamed, or never
                                    existed.
                                </p>

                                {/* Button */}
                                <Link to="/" className="not-found-btn">
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                        <polyline points="9 22 9 12 15 12 15 22" />
                                    </svg>
                                    Back to Home
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M5 12h14M12 5l7 7-7 7" />
                                    </svg>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <ScrollToTop />
            </Layout>
        </React.Fragment>
    );
};

export default NotFoundPage;
